import React from 'react';

import classNames from 'classnames';

import { formatAmount, formatCount, severityBadge } from './uploadFormat';

import Alert from 'CommonComponents/Alert';
import Badge from 'CommonComponents/Badge';
import Button from 'CommonComponents/Button';

import DownloadIcon from 'Assets/icons/download.svg';

function SummaryFigure({ label, count, value, tone }) {
    return (
        <div className="tw-rounded-lg tw-border tw-border-charcoal-15 tw-px-4 tw-py-3">
            <div className="tw-flex tw-items-center tw-gap-x-2 tw-text-xs tw-text-charcoal-75">
                {tone && (
                    <span
                        className={classNames('tw-inline-block tw-h-2 tw-w-2 tw-rounded-full', {
                            'tw-bg-green': tone === 'green',
                            'tw-bg-amber-400': tone === 'amber',
                            'tw-bg-red': tone === 'red',
                        })}
                        aria-hidden="true"
                    />
                )}
                {label}
            </div>
            <div className="tw-mt-1 tw-text-xl tw-font-semibold tw-text-charcoal">{formatCount(count)}</div>
            <div className="tw-text-xs tw-text-charcoal-50">{$t('Net value {{value}}', { value: formatAmount(value) })}</div>
        </div>
    );
}

/**
 * The proportion of rows that load cleanly, load with gaps, and are blocked.
 */
function SummaryBar({ counts }) {
    const clean = Math.max(0, counts.loadable - counts.gaps);
    const total = counts.read || 1;
    const segments = [
        { key: 'clean', count: clean, className: 'tw-bg-green' },
        { key: 'gaps', count: counts.gaps, className: 'tw-bg-amber-400' },
        { key: 'blocked', count: counts.blocked, className: 'tw-bg-red' },
    ].filter((segment) => segment.count > 0);

    return (
        <div
            className="tw-flex tw-h-2.5 tw-w-full tw-overflow-hidden tw-rounded-full tw-bg-charcoal-15"
            role="img"
            aria-label={$t('{{clean}} rows load cleanly, {{gaps}} load with gaps, {{blocked}} are blocked', {
                clean,
                gaps: counts.gaps,
                blocked: counts.blocked,
            })}
        >
            {segments.map((segment) => (
                <div key={segment.key} className={segment.className} style={{ width: `${(segment.count / total) * 100}%` }} />
            ))}
        </div>
    );
}

/**
 * Step 2. What was read, what will load, and every issue grouped by rule.
 */
export default function UploadValidationReport({ batch, issuesUrl, onContinue, onCancel }) {
    const { counts, values, issues } = batch.summary;
    const fileIssue = issues.find((issue) => issue.severity === 'file');
    const hasRowIssues = issues.some((issue) => issue.severity !== 'file');

    return (
        <div className="tw-flex tw-flex-col tw-gap-y-4">
            {batch.template_warning && (
                <Alert type="warning" role="status">
                    {batch.template_version
                        ? $t(
                              'This file was made from template version {{version}}. Its columns match, so it can be loaded, but download the new template for your next upload.',
                              {
                                  version: batch.template_version,
                              },
                          )
                        : $t(
                              'This file was made from an older template. Its columns match, so it can be loaded, but download the new template for your next upload.',
                          )}
                </Alert>
            )}

            <section className="tw-rounded-lg tw-bg-white tw-shadow-primary tw-px-6 tw-py-6.5">
                <div className="tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-3">
                    <div>
                        <h3 className="table__header">{$t('Validation report')}</h3>
                        <p className="tw-mt-1 tw-text-xs tw-text-charcoal-50">
                            {batch.file_name} · {batch.reference}
                        </p>
                    </div>
                    <Badge variant={counts.loadable > 0 ? 'success' : 'danger'} size="md">
                        {counts.loadable > 0 ? $t('Ready to review') : $t('Nothing can be loaded')}
                    </Badge>
                </div>

                {fileIssue ? (
                    <div className="tw-mt-4">
                        <Alert type="error">
                            <span className="tw-font-medium">{fileIssue.label}.</span> {fileIssue.action}
                        </Alert>
                    </div>
                ) : (
                    <>
                        <div className="tw-mt-4">
                            <SummaryBar counts={counts} />
                        </div>
                        <div className="tw-mt-4 tw-grid tw-grid-cols-2 tw-gap-3 md:tw-grid-cols-4">
                            <SummaryFigure label={$t('Rows read')} count={counts.read} value={values.read} />
                            <SummaryFigure label={$t('Will load')} count={counts.loadable} value={values.loadable} tone="green" />
                            <SummaryFigure label={$t('Will load with gaps')} count={counts.gaps} value={values.gaps} tone="amber" />
                            <SummaryFigure label={$t('Blocked')} count={counts.blocked} value={values.blocked} tone="red" />
                        </div>
                    </>
                )}
            </section>

            {hasRowIssues && (
                <section className="tw-rounded-lg tw-bg-white tw-shadow-primary tw-px-6 tw-py-6.5">
                    <div className="tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-3">
                        <div>
                            <h3 className="table__header">{$t('Issues')}</h3>
                            <p className="tw-mt-1 tw-text-xs tw-text-charcoal-50">
                                {$t(
                                    'Blocked rows are held back. Fix them in your file and upload the whole file again: rows already loaded show as unchanged.',
                                )}
                            </p>
                        </div>
                        <Button as="a" small variant="secondary" href={issuesUrl}>
                            <span className="tw-inline-flex tw-items-center tw-gap-x-1.5">
                                <DownloadIcon className="tw-h-3.5 tw-w-3.5" aria-hidden="true" />
                                {$t('Download issue report')}
                            </span>
                        </Button>
                    </div>
                    <div className="inbud-table_wrapper tw-mt-4">
                        <table className="inbud-table tw-w-full">
                            <thead>
                                <tr>
                                    <th>{$t('Issue')}</th>
                                    <th className="tw-w-36">{$t('Effect')}</th>
                                    <th className="tw-w-20 tw-text-end">{$t('Rows')}</th>
                                    <th className="tw-w-28 tw-text-end">{$t('First row')}</th>
                                    <th>{$t('Action required')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {issues
                                    .filter((issue) => issue.severity !== 'file')
                                    .map((issue) => {
                                        const badge = severityBadge(issue.severity);

                                        return (
                                            <tr key={issue.rule}>
                                                <td className="tw-font-medium">{issue.label}</td>
                                                <td>
                                                    <Badge variant={badge.variant}>{badge.label}</Badge>
                                                </td>
                                                <td className="tw-text-end">{formatCount(issue.count)}</td>
                                                <td className="tw-text-end">{issue.first_row ?? '-'}</td>
                                                <td className="tw-text-charcoal-75">{issue.action}</td>
                                            </tr>
                                        );
                                    })}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-2">
                <Button variant="secondary" onClick={onCancel}>
                    {$t('Cancel')}
                </Button>
                {counts.loadable > 0 && !fileIssue && (
                    <Button variant="primary" onClick={onContinue}>
                        {counts.blocked > 0
                            ? $t('Continue with {{count}} valid rows', { count: formatCount(counts.loadable) })
                            : $t('Continue with {{count}} rows', { count: formatCount(counts.loadable) })}
                    </Button>
                )}
            </div>
        </div>
    );
}
